import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './PendingApproval.scss';

interface PendingJob {
  id: number;
  title: string;
  company_name: string;
  location: string;
  status: string;
  created_at: string;
  employer_email: string;
  employer_first_name: string;
  employer_last_name: string;
}

interface PendingCV {
  id: number;
  title: string;
  first_name: string;
  last_name: string;
  email: string;
  location: string;
  status: string;
  created_at: string;
  updated_at: string;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
}

interface PendingTemplate {
  id: number;
  category: string;
  value: string;
  submitted_by: number;
  submitter_email: string;
  created_at: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  institution: 'Mokymo įstaigos',
  specialization: 'Specialybės',
  degree: 'Laipsniai',
  work_field: 'Darbo sritys',
  position: 'Pareigos',
  work_location: 'Darbo vietos',
  language: 'Kalbos',
};

const PendingApproval: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'jobs' | 'cvs' | 'templates'>('jobs');
  const [pendingJobs, setPendingJobs] = useState<PendingJob[]>([]);
  const [pendingCVs, setPendingCVs] = useState<PendingCV[]>([]);
  const [pendingTemplates, setPendingTemplates] = useState<PendingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [declineId, setDeclineId] = useState<{ id: number; type: 'job' | 'cv' } | null>(null);
  const [declineReason, setDeclineReason] = useState('');

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, cvsRes, templatesRes] = await Promise.all([
        api.get('/admin/pending-jobs'),
        api.get('/admin/pending-cvs'),
        api.get('/cv-templates/pending').catch(() => ({ data: [] }))
      ]);
      setPendingJobs(jobsRes.data.data || []);
      setPendingCVs(cvsRes.data.data || []);
      setPendingTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
    } catch (err) {
      console.error('Failed to load pending items:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = async (id: number, type: 'job' | 'cv') => {
    setActionLoading(id);
    try {
      const endpoint = type === 'job' ? `/admin/jobs/${id}/approve` : `/admin/cvs/${id}/approve`;
      await api.patch(endpoint);
      if (type === 'job') {
        setPendingJobs(prev => prev.filter(j => j.id !== id));
      } else {
        setPendingCVs(prev => prev.filter(c => c.id !== id));
      }
    } catch (err) {
      console.error('Approve failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineConfirm = async () => {
    if (!declineId) return;
    setActionLoading(declineId.id);
    try {
      const endpoint = declineId.type === 'job'
        ? `/admin/jobs/${declineId.id}/decline`
        : `/admin/cvs/${declineId.id}/decline`;
      await api.patch(endpoint, { reason: declineReason });
      if (declineId.type === 'job') {
        setPendingJobs(prev => prev.filter(j => j.id !== declineId.id));
      } else {
        setPendingCVs(prev => prev.filter(c => c.id !== declineId.id));
      }
    } catch (err) {
      console.error('Decline failed:', err);
    } finally {
      setActionLoading(null);
      setDeclineId(null);
      setDeclineReason('');
    }
  };

  const handleApproveTemplate = async (id: number) => {
    setActionLoading(id);
    try {
      await api.post(`/cv-templates/pending/${id}/approve`);
      setPendingTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('Template approve failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectTemplate = async (id: number) => {
    setActionLoading(id);
    try {
      await api.post(`/cv-templates/pending/${id}/reject`);
      setPendingTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('Template reject failed:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('lt-LT', { year: 'numeric', month: '2-digit', day: '2-digit' });

  const jobCount = pendingJobs.length;
  const cvCount = pendingCVs.length;
  const templateCount = pendingTemplates.length;

  return (
    <div className="pending-approval-page">
      <div className="page-header">
        <h1>Laukia patvirtinimo</h1>
        <span className="total-badge">{jobCount + cvCount + templateCount}</span>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          Darbo skelbimai
          {jobCount > 0 && <span className="tab-badge">{jobCount}</span>}
        </button>
        <button
          className={`tab ${activeTab === 'cvs' ? 'active' : ''}`}
          onClick={() => setActiveTab('cvs')}
        >
          CV
          {cvCount > 0 && <span className="tab-badge">{cvCount}</span>}
        </button>
        <button
          className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Šablonai
          {templateCount > 0 && <span className="tab-badge">{templateCount}</span>}
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Kraunama...</div>
      ) : activeTab === 'jobs' ? (
        jobCount === 0 ? (
          <div className="empty-state">Nėra laukiančių darbo skelbimų</div>
        ) : (
          <div className="pending-list">
            {pendingJobs.map(job => (
              <div key={job.id} className="pending-card">
                <div className="card-main">
                  <h3 className="card-title">{job.title}</h3>
                  <div className="card-meta">
                    <span>{job.company_name}</span>
                    <span className="separator">·</span>
                    <span>{job.location}</span>
                  </div>
                  <div className="card-submitter">
                    Pateikė: {job.employer_first_name} {job.employer_last_name} ({job.employer_email})
                  </div>
                  <div className="card-date">
                    Pateikta: {formatDate(job.created_at)}
                  </div>
                </div>
                <div className="card-actions">
                  <a
                    className="btn-preview"
                    href={`${window.location.hostname === 'localhost' ? 'http://localhost:3000' : ''}/job/${job.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Peržiūrėti
                  </a>
                  <button
                    className="btn-approve"
                    onClick={() => handleApprove(job.id, 'job')}
                    disabled={actionLoading === job.id}
                  >
                    {actionLoading === job.id ? '...' : 'Patvirtinti'}
                  </button>
                  <button
                    className="btn-decline"
                    onClick={() => setDeclineId({ id: job.id, type: 'job' })}
                    disabled={actionLoading === job.id}
                  >
                    Atmesti
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : activeTab === 'cvs' ? (
        cvCount === 0 ? (
          <div className="empty-state">Nėra laukiančių CV</div>
        ) : (
          <div className="pending-list">
            {pendingCVs.map(cv => (
              <div key={cv.id} className="pending-card">
                <div className="card-main">
                  <h3 className="card-title">{cv.first_name} {cv.last_name}</h3>
                  <div className="card-meta">
                    <span>{cv.title || 'CV'}</span>
                    {cv.location && (
                      <>
                        <span className="separator">·</span>
                        <span>{cv.location}</span>
                      </>
                    )}
                  </div>
                  <div className="card-submitter">
                    Pateikė: {cv.user_first_name} {cv.user_last_name} ({cv.user_email})
                  </div>
                  <div className="card-date">
                    Pateikta: {formatDate(cv.updated_at || cv.created_at)}
                  </div>
                </div>
                <div className="card-actions">
                  <button
                    className="btn-approve"
                    onClick={() => handleApprove(cv.id, 'cv')}
                    disabled={actionLoading === cv.id}
                  >
                    {actionLoading === cv.id ? '...' : 'Patvirtinti'}
                  </button>
                  <button
                    className="btn-decline"
                    onClick={() => setDeclineId({ id: cv.id, type: 'cv' })}
                    disabled={actionLoading === cv.id}
                  >
                    Atmesti
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        templateCount === 0 ? (
          <div className="empty-state">Nėra laukiančių šablonų patvirtinimui</div>
        ) : (
          <div className="pending-list">
            {pendingTemplates.map(tpl => (
              <div key={tpl.id} className="pending-card">
                <div className="card-main">
                  <h3 className="card-title">{tpl.value}</h3>
                  <div className="card-meta">
                    <span className="template-category-badge">{CATEGORY_LABELS[tpl.category] || tpl.category}</span>
                  </div>
                  <div className="card-submitter">
                    Pateikė: {tpl.submitter_email}
                  </div>
                  <div className="card-date">
                    Pateikta: {formatDate(tpl.created_at)}
                  </div>
                </div>
                <div className="card-actions">
                  <button
                    className="btn-approve"
                    onClick={() => handleApproveTemplate(tpl.id)}
                    disabled={actionLoading === tpl.id}
                  >
                    {actionLoading === tpl.id ? '...' : 'Patvirtinti'}
                  </button>
                  <button
                    className="btn-decline"
                    onClick={() => handleRejectTemplate(tpl.id)}
                    disabled={actionLoading === tpl.id}
                  >
                    Atmesti
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Decline modal */}
      {declineId && (
        <div className="decline-overlay" onClick={() => setDeclineId(null)}>
          <div className="decline-modal" onClick={e => e.stopPropagation()}>
            <h3>Atmesti {declineId.type === 'job' ? 'skelbimą' : 'CV'}?</h3>
            <label>
              Priežastis (neprivaloma):
              <textarea
                value={declineReason}
                onChange={e => setDeclineReason(e.target.value)}
                rows={3}
                placeholder="Įveskite priežastį..."
              />
            </label>
            <div className="decline-actions">
              <button className="btn-cancel" onClick={() => setDeclineId(null)}>Atšaukti</button>
              <button className="btn-decline-confirm" onClick={handleDeclineConfirm}>
                Atmesti
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingApproval;
